export type ESPNAccessRequestStatus = "pending" | "approved" | "denied";
export type ESPNAccessRequestTargetType = "league" | "team";
export type ESPNAccessRequestDecision = "approve" | "deny";

export interface ESPNAccessRequest {
  id: string;
  userID: string;
  status: ESPNAccessRequestStatus;
  targetType: ESPNAccessRequestTargetType;
  sportID: string;
  leagueID: string;
  leagueName: string;
  espnLeagueSlug: string | null;
  teamID: string | null;
  teamName: string | null;
  espnTeamID: string | null;
  name: string;
  email: string;
  phone: string;
  message: string;
  createdAt: Date;
  updatedAt: Date;
  decidedBy: string | null;
  decidedAt: Date | null;
  denyReason: string | null;
}

export class ESPNAccessRequestDecodeError extends Error {
  readonly fieldPath: string;

  constructor(fieldPath: string, expected: string) {
    super(`Invalid ESPN access response at ${fieldPath}: expected ${expected}.`);
    this.name = "ESPNAccessRequestDecodeError";
    this.fieldPath = fieldPath;
  }
}

const STATUSES: readonly ESPNAccessRequestStatus[] = ["pending", "approved", "denied"];
const TARGET_TYPES: readonly ESPNAccessRequestTargetType[] = ["league", "team"];

export function espnAccessRequestTargetDisplayName(request: ESPNAccessRequest): string {
  return request.targetType === "team" ? (request.teamName ?? request.leagueName) : request.leagueName;
}

export function decodeESPNAccessRequest(value: unknown, path = "request"): ESPNAccessRequest {
  const data = record(value, path);
  const targetType = enumValue(data.targetType, TARGET_TYPES, `${path}.targetType`);
  const teamID = nullableString(data.teamID, `${path}.teamID`);
  const teamName = nullableString(data.teamName, `${path}.teamName`);
  if (targetType === "team" && (!teamID || !teamName)) {
    throw new ESPNAccessRequestDecodeError(path, "team requests with teamID and teamName");
  }

  return {
    id: nonEmptyString(data.id, `${path}.id`),
    userID: nonEmptyString(data.userID, `${path}.userID`),
    status: enumValue(data.status, STATUSES, `${path}.status`),
    targetType,
    sportID: nonEmptyString(data.sportID, `${path}.sportID`),
    leagueID: nonEmptyString(data.leagueID, `${path}.leagueID`),
    leagueName: nonEmptyString(data.leagueName, `${path}.leagueName`),
    espnLeagueSlug: nullableString(data.espnLeagueSlug, `${path}.espnLeagueSlug`),
    teamID,
    teamName,
    espnTeamID: nullableString(data.espnTeamID, `${path}.espnTeamID`),
    name: nonEmptyString(data.name, `${path}.name`),
    email: nonEmptyString(data.email, `${path}.email`),
    phone: nonEmptyString(data.phone, `${path}.phone`),
    message: nonEmptyString(data.message, `${path}.message`),
    createdAt: decodeESPNAccessDate(data.createdAt, `${path}.createdAt`),
    updatedAt: decodeESPNAccessDate(data.updatedAt, `${path}.updatedAt`),
    decidedBy: nullableString(data.decidedBy, `${path}.decidedBy`),
    decidedAt: nullableDate(data.decidedAt, `${path}.decidedAt`),
    denyReason: nullableString(data.denyReason, `${path}.denyReason`),
  };
}

export function decodeESPNAccessRequestEnvelope(value: unknown): ESPNAccessRequest {
  const data = record(value, "response");
  return decodeESPNAccessRequest(data.request, "response.request");
}

export function decodeESPNAccessRequestListEnvelope(value: unknown): ESPNAccessRequest[] {
  const data = record(value, "response");
  if (!Array.isArray(data.requests)) {
    throw new ESPNAccessRequestDecodeError("response.requests", "an array");
  }
  return data.requests.map((request, index) =>
    decodeESPNAccessRequest(request, `response.requests[${index}]`));
}

export function decodeESPNAccessDate(value: unknown, path = "date"): Date {
  if (
    typeof value !== "string"
    || !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value)
  ) {
    throw new ESPNAccessRequestDecodeError(path, "an ISO-8601 UTC timestamp");
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime()) || date.toISOString() !== value) {
    throw new ESPNAccessRequestDecodeError(path, "a valid ISO-8601 UTC timestamp");
  }
  return date;
}

function nullableDate(value: unknown, path: string): Date | null {
  return value === null ? null : decodeESPNAccessDate(value, path);
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function record(value: unknown, path: string): Record<string, unknown> {
  if (!isRecord(value)) throw new ESPNAccessRequestDecodeError(path, "an object");
  return value;
}

function nonEmptyString(value: unknown, path: string): string {
  if (typeof value !== "string" || !value.trim()) {
    throw new ESPNAccessRequestDecodeError(path, "a non-empty string");
  }
  return value;
}

function nullableString(value: unknown, path: string): string | null {
  if (value === null) return null;
  if (typeof value !== "string") throw new ESPNAccessRequestDecodeError(path, "a string or null");
  return value;
}

function enumValue<T extends string>(
  value: unknown,
  allowed: readonly T[],
  path: string,
): T {
  if (typeof value !== "string" || !allowed.includes(value as T)) {
    throw new ESPNAccessRequestDecodeError(
      path,
      allowed.map((candidate) => `"${candidate}"`).join(" or "),
    );
  }
  return value as T;
}
